<div class="mx-auto max-w-7xl p-6">
    <h1 class="mb-6 text-3xl font-bold text-gray-900">Number Input Component Testing</h1>

    <div class="mb-6">
        <p class="mb-4 text-gray-700">
            This page tests various combinations of format and decimals properties for both app-control and number-input
            components. Try editing the values to test decimal input functionality with different formats.
        </p>

        <div class="mb-6 flex gap-4">
            <button class="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700" (click)="resetValues()">
                Reset with Random Values
            </button>
            <button class="rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700" (click)="clearValues()">
                Clear All Values
            </button>
        </div>
    </div>

    <div class="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <!-- App Control Component Tests -->
        <div class="rounded-lg bg-white p-6 shadow">
            <h2 class="mb-4 text-2xl font-semibold text-gray-800">App Control Component</h2>

            @for (testCase of testCases(); track testCase.name) {
                @if (testCase.componentType === 'app-control') {
                    <div class="mb-6 rounded border border-gray-200 p-4">
                        <h3 class="mb-2 text-lg font-medium text-gray-700">{{ testCase.name }}</h3>
                        <p class="mb-3 text-sm text-gray-600">{{ testCase.description }}</p>

                        <div class="mb-3 grid grid-cols-2 gap-4">
                            <div class="rounded p-3">
                                <h4 class="mb-2 text-sm font-medium text-gray-600">View Mode</h4>
                                <app-control
                                    [id]="getInputId(testCase.name) + '-view'"
                                    [type]="'number'"
                                    [label]="testCase.name + ' (View)'"
                                    [format]="testCase.format"
                                    [decimals]="testCase.decimals"
                                    [min]="testCase.min"
                                    [max]="testCase.max"
                                    [isEditMode]="false"
                                    [(ngModel)]="testCase.value"></app-control>
                            </div>
                            <div class="rounded p-3">
                                <h4 class="mb-2 text-sm font-medium text-gray-600">Edit Mode</h4>
                                <app-control
                                    [id]="getInputId(testCase.name) + '-edit'"
                                    [type]="'number'"
                                    [label]="testCase.name + ' (Edit)'"
                                    [format]="testCase.format"
                                    [decimals]="testCase.decimals"
                                    [min]="testCase.min"
                                    [max]="testCase.max"
                                    [isEditMode]="true"
                                    [(ngModel)]="testCase.value"
                                    (ngModelChange)="onValueChange(testCase, $event)"></app-control>
                            </div>
                        </div>

                        <div class="text-xs text-gray-500">
                            <div>
                                <strong>Current Value:</strong>
                                {{ testCase.value }}
                            </div>
                            <div>
                                <strong>Format:</strong>
                                {{ getFormatType(testCase.format) }}
                            </div>
                            <div>
                                <strong>Decimals:</strong>
                                {{ getDecimalsDisplay(testCase.decimals) }}
                            </div>
                            @if (isNumberFormatOptions(testCase.format)) {
                                <div>
                                    <strong>Format Details:</strong>
                                    {{ testCase.format | json }}
                                </div>
                            }
                        </div>
                    </div>
                }
            }
        </div>

        <!-- Number Input Component Tests -->
        <div class="rounded-lg bg-white p-6 shadow">
            <h2 class="mb-4 text-2xl font-semibold text-gray-800">Number Input Component</h2>

            @for (testCase of testCases(); track testCase.name) {
                @if (testCase.componentType === 'number-input') {
                    <div class="mb-6 rounded border border-gray-200 p-4">
                        <h3 class="mb-2 text-lg font-medium text-gray-700">{{ testCase.name }}</h3>
                        <p class="mb-3 text-sm text-gray-600">{{ testCase.description }}</p>

                        <div class="mb-3 grid grid-cols-2 gap-4">
                            <div class="rounded p-3">
                                <h4 class="mb-2 text-sm font-medium text-gray-600">View Mode</h4>
                                <app-number-input
                                    [id]="getInputId(testCase.name) + '-view'"
                                    [label]="testCase.name + ' (View)'"
                                    [format]="testCase.format"
                                    [decimals]="testCase.decimals"
                                    [min]="testCase.min"
                                    [max]="testCase.max"
                                    [isEditMode]="false"
                                    [(value)]="testCase.value"></app-number-input>
                            </div>
                            <div class="rounded p-3">
                                <h4 class="mb-2 text-sm font-medium text-gray-600">Edit Mode</h4>
                                <app-number-input
                                    [id]="getInputId(testCase.name) + '-edit'"
                                    [label]="testCase.name + ' (Edit)'"
                                    [format]="testCase.format"
                                    [decimals]="testCase.decimals"
                                    [min]="testCase.min"
                                    [max]="testCase.max"
                                    [isEditMode]="true"
                                    [(value)]="testCase.value"
                                    (valueChange)="onValueChange(testCase, $event)"></app-number-input>
                            </div>
                        </div>

                        <div class="text-xs text-gray-500">
                            <div>
                                <strong>Current Value:</strong>
                                {{ testCase.value }}
                            </div>
                            <div>
                                <strong>Format:</strong>
                                {{ getFormatType(testCase.format) }}
                            </div>
                            <div>
                                <strong>Decimals:</strong>
                                {{ getDecimalsDisplay(testCase.decimals) }}
                            </div>
                            @if (isNumberFormatOptions(testCase.format)) {
                                <div>
                                    <strong>Format Details:</strong>
                                    {{ testCase.format | json }}
                                </div>
                            }
                        </div>
                    </div>
                }
            }
        </div>
    </div>

    <!-- Instructions -->
    <div class="mt-8 rounded-lg border border-blue-200 bg-blue-50 p-6">
        <h3 class="mb-3 text-lg font-semibold text-blue-800">Testing Instructions</h3>
        <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div>
                <h4 class="mb-2 font-semibold text-blue-700">Manual Testing</h4>
                <ul class="list-inside list-disc space-y-2 text-blue-700">
                    <li>Try entering decimal values in each input field</li>
                    <li>Test edge cases like very small decimals (0.001) and large numbers</li>
                    <li>Check if the decimal precision matches the expected format</li>
                    <li>Compare behavior between app-control and number-input components</li>
                    <li>Verify that NumberFormatOptions (currency formats) work correctly</li>
                    <li>Test that decimals property overrides format when both are provided</li>
                </ul>
            </div>
            <div>
                <h4 class="mb-2 font-semibold text-blue-700">Component Comparison</h4>
                <ul class="list-inside list-disc space-y-2 text-blue-700">
                    <li>Compare behavior between app-control and number-input components</li>
                    <li>View mode shows formatted display values</li>
                    <li>Edit mode shows input field behavior</li>
                    <li>Test different format configurations side by side</li>
                </ul>
            </div>
        </div>
    </div>
</div>
